/**
 * Services for getting data from db via http rest
 *
 * @author Mamontov Andrey, 08/2019
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { RestConfig } from '../shared/configs/rest.config';
import { AppUser, Role, RolePermission } from '../models/user.model';
import { AppError, BaseDictionary, Structure, Template } from '../models/app.model';
import { Member, VpDictionary } from '../models/vpo.model';
import { CommonDocument, DocumentFile, DocumentImage, FaqDocument } from '../models/document.model';
import { Observable, Subject } from 'rxjs';
import { Complaint } from '../models/reception.model';
import { CarFinanceObject, CommonFinanceDocument, LandFinanceObject} from '../models/finance-document.model';
import { tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';



/**
 * Attention! All returned results here are Observable!!!
 */
@Injectable()
export class RestService {

  headers: HttpHeaders;

  constructor(private _http: HttpClient) {
    this.headers = undefined;
  }

  /**
   * Universal section
   */
  getVersion() {
    return this._http.get(RestConfig.REST_URL + '/version');
  }


  /**
   * Universal section
   */
  getData(link: string, type:  string = 'get', data: any = null) {
    if (type === 'get') {
      return this._http.get(link);
    }
    if (type === 'post') {
      return this._http.post(link, data);
    }
  }

  // remove data
  removeData(link: string, id: string) {
    return this._http.delete(RestConfig.REST_URL + link + '/' + id);
  }

  //
  getDocuments(type: string, page: number = 1, isMainPage: number = 1) {
    if (!type) { return; }
    let pg = page ? '/?s=' + page : '';
    pg = pg && isMainPage ? pg + '&mainPage=' + isMainPage : pg;
    pg = !pg && isMainPage ? '?mainPage=' + isMainPage : pg;
    return this._http.get(RestConfig.REST_URL + '/' + type + pg);
  }

  filterDocuments(type: string, filter: string, page: number = 1) {
    if (!type) { return; }
    const query = '/?' + filter + '&s=' + page;
    return this._http.get(RestConfig.REST_URL + '/' + type + '/search' + query);
  }

  /**
   * Common rest for documents
   */

  /**
   * Common rest
   * @param type (news | veteran_news )
   */
  getAllDocuments(type: string, filterYear: string = '', page: number = 0) {
    let filter = filterYear ? '?year=' + filterYear : '';
    filter += page && filter ? `&page=${page}` : page && !filter ? `?page=${page}` : '';
    return this._http.get(RestConfig.REST_URL + '/' + type + '_all' + filter);
  }

  getDocumentsYears(type: string) {
    return this._http.get(RestConfig.REST_URL + '/' + type + '_years');
  }

  /*getAllDocumentsP(type: string) {
    return this._http.get(RestConfig.REST_URL + '/' + type);
  }*/

  addDocument(n: CommonDocument, type: string) {
    return this._http.post(RestConfig.REST_URL + '/' + type, n);
  }

  updateDocument(id: string, type: string, n: CommonDocument) {
    return this._http.put(RestConfig.REST_URL + '/' + type +  '/' + id, n);
  }

  deleteDocument(id: string, type: string) {
    return this._http.delete(RestConfig.REST_URL + '/' + type +  '/' + id);
  }

  getOneDocument(id: string, type: string) {
    return this._http.get(RestConfig.REST_URL + '/' + type + '/' + id);
  }

  addDocumentsImages(id: string, type: string, images: Array<DocumentImage>) {
    return this._http.post(RestConfig.REST_URL + RestConfig.DOCUMENTS + '/' + id + '/' + type + '/images', images);
  }

  addDocumentsFiles(id: string, type: string, files: Array<DocumentFile>) {
    return this._http.post(RestConfig.REST_URL + RestConfig.DOCUMENTS + '/' + id + '/' + type + '/files', files);
  }

  deleteDocumentsImage(type: string, imageId: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.DOCUMENTS + '/' + type + '/images/' + imageId);
  }

  deleteDocumentsFile(type: string, fileId: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.DOCUMENTS + '/' + type + '/files/' + fileId);
  }

  deleteDocumentsFileFromFs(type: string, file: DocumentFile) {
    return this._http.post(RestConfig.REST_URL + RestConfig.DOCUMENTS + '/' + type + '/files/fs', file);
  }

  getDocumentImages(id: string, type: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.DOCUMENTS + '/' + id + '/' + type + '/images');
  }

  getDocumentFiles(id: string, type: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.DOCUMENTS + '/' + id + '/' + type + '/files');
  }

  /**
   * Vpo
   */
  getVpo() {
    return this._http.get(RestConfig.REST_URL + RestConfig.VPO);
  }

  getVpoActive() {
    return this._http.get(RestConfig.REST_URL + RestConfig.GET_VPO_ACTIVE);
  }

  getVpoByCode(code: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.VPO + '/' + code);
  }

  getOneVpo(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.VPO + '/' + id);
  }

  /**
   * Roles
   */
  getRoles() {
    return this._http.get(RestConfig.REST_URL + RestConfig.ROLES);
  }

  getOneRole(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.ROLES + '/' + id);
  }

  getOneRolePermissions(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.ROLES + '/' + id + '/permissions');
  }

  getEnabledRolePermissions(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.ROLES + '/permissions_enabled/' + id);
  }

  addRole(role: Role) {
    return this._http.post(RestConfig.REST_URL + RestConfig.ROLES, role);
  }

  updateRole(id: string, role: Role) {
    return this._http.put(RestConfig.REST_URL + RestConfig.ROLES + '/' + id, role);
  }

  addRolePermissions(id: string, permissions: Array<RolePermission>) {
    return this._http.post(RestConfig.REST_URL + RestConfig.ROLES + '/' + id + '/permissions', permissions);
  }

  deleteRole(id: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.ROLES + '/' + id);
  }

  deleteRolePermission(idRole: string, idPermission: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.ROLES + '/' + idRole + '/permissions/' + idPermission);
  }

  /**
   * Structures
   */
  getStructures() {
    return this._http.get(RestConfig.REST_URL + RestConfig.STRUCTURE);
  }

  getFrontStructures() {
    return this._http.get(RestConfig.REST_URL + RestConfig.STRUCTURE + '/front');
  }

  getOneStructure(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.STRUCTURE + '/' + id);
  }

  addStructure(s: Structure) {
    return this._http.post(RestConfig.REST_URL + RestConfig.STRUCTURE, s);
  }

  updateStructure(s: Structure, id: string) {
    return this._http.put(RestConfig.REST_URL + RestConfig.STRUCTURE + '/' + id, s);
  }

  deleteStructure(id: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.STRUCTURE + '/' + id);
  }

  /**
   * Faq
   */
  getAllFaq() {
    return this._http.get(RestConfig.REST_URL + RestConfig.FAQ + '_all');
  }

  /*getAllFaqP(page: number = 1) {
    const pg = page ? '/?s=' + page : '';
    return this._http.get(RestConfig.REST_URL + RestConfig.FAQ  + '/' + pg);
  }*/

  addFaq(f: FaqDocument) {
    return this._http.post(RestConfig.REST_URL + RestConfig.FAQ, f);
  }

  updateFaq(id: string, f: FaqDocument) {
    return this._http.put(RestConfig.REST_URL + RestConfig.FAQ + '/' + id, f);
  }

  deleteFaq(id: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.FAQ +  '/' + id);
  }

  getOneFaq(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.FAQ + '/' + id);
  }

  /**
   * Finance
   */
  addOneFinance(finance: CommonFinanceDocument) {
    return this._http.post(RestConfig.REST_URL + '/incomes', finance);
  }

  updateOneFinance(id: string, finance: CommonFinanceDocument) {
    return this._http.put(RestConfig.REST_URL + '/incomes/' + id, finance);
  }

  getOneFinanceByIdYear(id: string, year: number) {
    return this._http.get(RestConfig.REST_URL + '/finances/' + id + '?year=' + year);
  }

  getOneFinanceById(id: string) {
    return this._http.get(RestConfig.REST_URL + '/incomes/' + id);
  }

  deleteOneFinanceById(id: string) {
    return this._http.delete(RestConfig.REST_URL + '/incomes/' + id);
  }

  getOneFinanceLandsById(id: string) {
    return this._http.get(RestConfig.REST_URL + '/incomes_lands/' + id);
  }

  addOneFinanceLand(financeDocumentId: string, land: LandFinanceObject) {
    return this._http.post(RestConfig.REST_URL + '/incomes_lands/' + financeDocumentId, land);
  }

  updateOneFinanceLand(landId: string, land: LandFinanceObject) {
    return this._http.put(RestConfig.REST_URL + '/incomes_lands/' + landId, land);
  }

  deleteOneFinanceLand(landId: string) {
    return this._http.delete(RestConfig.REST_URL + '/incomes_lands/' + landId);
  }

  getOneFinanceCarsById(id: string) {
    return this._http.get(RestConfig.REST_URL + '/incomes_cars/' + id);
  }

  addOneFinanceCar(id: string, car: CarFinanceObject) {
    return this._http.post(RestConfig.REST_URL + '/incomes_cars/' + id, car);
  }

  updateOneFinanceCar(id: string, car: CarFinanceObject) {
    return this._http.put(RestConfig.REST_URL + '/incomes_cars/' + id, car);
  }

  deleteOneFinanceCar(id: string) {
    return this._http.delete(RestConfig.REST_URL + '/incomes_cars/' + id);
  }

  getFinanceLandTypes() {
    return this._http.get(RestConfig.REST_URL + '/incomes_lands_types');
  }

  getFinanceLandNames() {
    return this._http.get(RestConfig.REST_URL + '/incomes_lands_names');
  }

  getFinanceLandAddresses() {
    return this._http.get(RestConfig.REST_URL + '/incomes_lands_addresses');
  }

  getFinanceCarTypes() {
    return this._http.get(RestConfig.REST_URL + '/incomes_cars_types');
  }

  getFinanceCarNames() {
    return this._http.get(RestConfig.REST_URL + '/incomes_cars_names');
  }

  getFinanceFamilyMembers(id, year) {
    return this._http.get(RestConfig.REST_URL + '/incomes/members/' + id + '?year=' + year);
  }

  /**
   * Templates
   */
  getTemplates() {
    return this._http.get(RestConfig.REST_URL + RestConfig.TEMPLATE);
  }

  getTemplatesByFilter(filter: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.TEMPLATE + '/filter/' + filter);
  }

  getOneTemplate(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.TEMPLATE + '/' + id);
  }

  getOneTemplateByIdStruct(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.TEMPLATE + '/structure/' + id);
  }

  getTemplatesByPrefixPath(path: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.TEMPLATE + '/path/' + path);
  }

  addTemplate(t: Template) {
    return this._http.post(RestConfig.REST_URL + RestConfig.TEMPLATE, t);
  }

  updateTemplate(t: Template, id: string) {
    return this._http.put(RestConfig.REST_URL + RestConfig.TEMPLATE + '/' + id, t);
  }

  deleteTemplate(id: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.TEMPLATE + '/' + id);
  }

  /**
   * Users
   */
  getUsers() {
    return this._http.get(RestConfig.REST_URL + RestConfig.USERS);
  }

  getOneUser(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.USERS + '/' + id);
  }

  addUser(u: AppUser) {
    return this._http.post(RestConfig.REST_URL + RestConfig.USERS, u);
  }

  updateUser(id: string, u: AppUser) {
    return this._http.put(RestConfig.REST_URL + RestConfig.USERS + '/' + id, u);
  }

  updateUserPassword(id: string, password: string) {
    return this._http.put(RestConfig.REST_URL + RestConfig.USERS + '/' + id + '/change_password', { password });
  }

  deleteUser(id: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.USERS + '/' + id);
  }

  /**
   * Dictionaries
   */
  // vp
  getVpDictionariesData() {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP);
  }

  // vp
  getVpDictionariesActiveData() {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP_ACTIVE);
  }

  //
  getVpDictionariesActiveDataP(page: number = 1, idVpo: string = '') {
    let pg = page ? '/?s=' + page : '';
    pg = idVpo ? pg + '&v=' + idVpo : pg;
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP_ACTIVE_PAGEABLE + pg);
  }

  getOneVpDictionaryElement(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP + '/' + id);
  }

  addVpDictionaryElement(v: VpDictionary) {
    return this._http.post(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP, v);
  }

  updateVpDictionaryElement(id: string, v: VpDictionary) {
    return this._http.put(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP + '/' + id, v);
  }

  deleteVpDictionaryElement(id: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP + '/' + id);
  }

  // members
  getMembersDictionariesData(modeLeaders: boolean = false) {
    if (modeLeaders) {
      return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_LEADERS);
    } else {
      return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_MEMBERS);
    }
  }

  getMembersDictionariesActiveData() {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_MEMBERS_ACTIVE);
  }

  getMembersDictionariesTotalData(id: string = '') {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_MEMBERS_TOTAL + (id ? '/' + id : ''));
  }

  // leaders by vpo
  getLeadersData() {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_LEADERS + '/vpo');
  }

  getOneMembersDictionaryElement(id: string, modeLeaders: boolean = false) {
    if (modeLeaders) {
      return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_LEADERS + '/' + id);
    } else {
      return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_MEMBERS + '/' + id);
    }
  }

  addMembersDictionaryElement(m: Member) {
    return this._http.post(RestConfig.REST_URL + RestConfig.DICTIONARIES_MEMBERS, m);
  }

  updateMembersDictionaryElement(id: string, m: Member, modeLeaders: boolean = false) {
    if (modeLeaders) {
      return this._http.put(RestConfig.REST_URL + RestConfig.DICTIONARIES_LEADERS + '/' + id, m);
    } else {
      return this._http.put(RestConfig.REST_URL + RestConfig.DICTIONARIES_MEMBERS + '/' + id, m);
    }
  }

  // base dictionary
  getBasesDictionariesData(type: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_BASE + type);
  }

  getOneBaseDictionaryElement(id: string, type: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_BASE + type + '/' + id);
  }

  addBaseDictionaryElement(d: BaseDictionary, type: string) {
    return this._http.post(RestConfig.REST_URL + RestConfig.DICTIONARIES_BASE + type, d);
  }

  updateBaseDictionaryElement(id: string, d: BaseDictionary, type: string) {
    return this._http.put(RestConfig.REST_URL + RestConfig.DICTIONARIES_BASE + type + '/' + id, d);
  }

  deleteBaseDictionaryElement(id: string, type: string) {
    return this._http.delete(RestConfig.REST_URL + RestConfig.DICTIONARIES_BASE + type + '/' + id);
  }

  /**
   * Receptions
   */
  //
  getComplaintThemesDictionary() {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_COMPLAINTS_THEMES);
  }

  getComplaintVp() {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_VP + '_active_complaint');
  }

  /**
   * Получение списка регионов России
   * @see https://services.pbprog.ru/doc/apiFias/region.html
   */
  getComplaintRegions() {
    return this._http.get(RestConfig.REST_URL + RestConfig.DICTIONARIES_COMPLAINTS_REGIONS);
  }

  addComplaint(c: Complaint) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints', c);
  }

  findComplaint(code: string) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/find', { code });
  }

  checkComplaint(email: string) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/check_complaint', { email });
  }

  getComplaintStats(year: number) {
    const query = year ? '/?year=' + year : '';
    return this._http.get(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints_stats' + query);
  }

  getComplaintEppStats(date: string = '') {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints_epp_stats', { date });
  }

  getComplaintById(id: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/' + id);
  }

  setSpamOrNotSpam(id: string, value: number) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/' + id + '/set_spam', { id, value });
  }

  setSOOrNotSO(id: string, value: number) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/' + id + '/set_so', { id, value });
  }

  getComplaintIdsByFilter(filter: string) {
    if (!filter) { return; }
    const query = '/?' + filter;
    return this._http.get(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints_get_ids' + query);
  }

  forwardComplaintById(id: string) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/forward', {id: id});
  }

  forwardComplaintAll() {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/forward_all', {});
  }

  testComplaint() {
    return this._http.get(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/test');
  }

  checkEmailAddressComplaint(data) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/email_check', data);
  }

  requestPrintingComplaints(payload: {}) {
    return this._http.post(RestConfig.REST_URL + RestConfig.RECEPTION + '/complaints/print', payload);
  }

  /**
   * Memory Book
   */
  getAllMemoryBookRecords() {
    return this._http.get(RestConfig.REST_URL + RestConfig.MEMORY_BOOK);
  }

  /**
   * Authorize
   */
  authenticate(login: string, password: string) {
    return this._http.post(RestConfig.REST_URL + RestConfig.AUTH, {'name': login, 'password': password});
  }

  // @see https://github.com/LukasMarx/angular-file-upload/blob/master/client/src/app/upload/upload.service.ts
  fileUpload(files: Set<File>, uploadPath: string, data: any = []): { [key: string]: { progress: Observable<number> } } {

    // this will be the our resulting map
    const status: { [key: string]: { progress: Observable<number> } } = {};

    files.forEach(file => {
      // create a new multipart-form for every file
      const formData: FormData = new FormData();
      formData.append('field', JSON.stringify({path: uploadPath, data: data}));
      formData.append('file', file, file.name);
      // create a http-post request and pass the form
      // tell it to report the upload progress
      const req = new HttpRequest('POST', RestConfig.REST_URL + RestConfig.DOCUMENTS_FILES_UPLOAD, formData, {
        reportProgress: true
      });

      // create a new progress-subject for every file
      const progress = new Subject<number>();

      // send the http-request and subscribe for progress-updates

      const startTime = new Date().getTime();
      this._http.request(req).subscribe(event => {
        if (event.type === HttpEventType.UploadProgress) {
          // calculate the progress percentage
          const percentDone = Math.round((100 * event.loaded) / event.total);
          // pass the percentage into the progress-stream
          progress.next(percentDone);
        } else if (event instanceof HttpResponse) {
          // Close the progress-stream if we get an answer form the API
          // The upload is complete
          progress.complete();
        }
      });

      // Save every progress-observable in a map of all observables
      status[file.name] = {
        progress: progress.asObservable()
      };
    });
    // return the map of progress.observables
    return status;
  }

  /**
   * Static files
   */
  getOneFile(filename: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.GET_ONE_FILE + filename, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      tap(res => res.status === 200 && saveAs(res.body, filename))
    );
  }

  getOneRegisterFile(filename: string) {
    return this._http.get(RestConfig.REST_URL + RestConfig.GET_ONE_REGISTER_FILE + filename, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      tap(res => res.status === 200 && saveAs(res.body, filename))
    );
  }

  downloadEppData(filename: string) {
    return this._http.post(RestConfig.REST_URL + RestConfig.GET_EPP_FILE, {}, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      tap(res => res.status === 200 && saveAs(res.body, filename))
    );
  }

  /**
   * Logger
   */
  getLastLoggerData() {
    return this._http.get(RestConfig.REST_URL + '/logger_last');
  }

  /**
   * App Errors
   */
  getAppErrors() {
    return this._http.get(RestConfig.REST_URL + '/app_errors');
  }

  addAppErrors(e: AppError) {
    return this._http.post(RestConfig.REST_URL + '/app_errors', e);
  }

  updateAppErrors(id: string, e: AppError) {
    return this._http.put(RestConfig.REST_URL + '/app_errors/' + id, e);
  }

  removeAppErrors(id: string) {
    return this._http.delete(RestConfig.REST_URL + '/app_errors/' + id);
  }


}
